import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Footer from "@/components/berlin/Footer";
import { BrandWordmark } from "@/components/berlin/BrandWordmark";

/*
  Datenschutzerklärung · DSGVO Art. 13
  Angepasst auf die tatsächliche Datenverarbeitung der Website:
  - Kontaktformular (POST /api/contact → Name, Organisation, E-Mail, Art des Ortes, Länge, Nachricht, Unterlagen, Honeypot)
  - Kein Analytics, keine Cookies, keine Tracker
  - Hosting auf Preview-Umgebung / später eigenes Hosting
*/

const Section = ({ n, title, children }) => (
  <section className="pb-10 border-b border-white/10 last:border-b-0">
    <div className="flex items-baseline gap-6 mb-4">
      <span className="text-[10px] tracking-[0.35em] uppercase text-[#F5D77A] font-display shrink-0 w-10">
        {n}
      </span>
      <h2 className="font-display font-light tracking-[-0.015em] text-white text-xl md:text-2xl">
        {title}
      </h2>
    </div>
    <div className="pl-16 space-y-3 text-white/75 text-sm md:text-[15px] leading-relaxed">
      {children}
    </div>
  </section>
);

export default function Datenschutz() {
  return (
    <div className="min-h-screen bg-[#1F2328] text-white" data-testid="page-datenschutz">
      <header className="border-b border-white/8">
        <div className="max-w-[1400px] mx-auto px-6 md:px-14 py-6 flex items-center justify-between">
          <Link
            to="/"
            className="inline-flex items-center gap-2 font-display font-semibold tracking-[0.24em] text-sm text-white hover:text-[#F5D77A] transition-colors"
            data-testid="datenschutz-nav-home"
          >
            <BrandWordmark lineClass="bg-white/80" />
          </Link>
          <Link to="/" className="inline-flex items-center gap-2 text-[11px] tracking-[0.32em] uppercase text-white/60 hover:text-white transition-colors">
            <ArrowLeft className="h-3.5 w-3.5" strokeWidth={1.5} />
            Zurück
          </Link>
        </div>
      </header>

      <main className="max-w-[900px] mx-auto px-6 md:px-14 py-20 md:py-32">
        <div className="text-[11px] tracking-[0.35em] uppercase text-white/45 mb-8 flex items-center gap-3">
          <span className="h-px w-8 bg-[#F5D77A]" />
          DSGVO
        </div>
        <h1
          className="font-display font-light tracking-[-0.03em] leading-[1] text-white mb-8"
          style={{ fontSize: "clamp(2.5rem, 6vw, 5rem)" }}
        >
          Datenschutz&shy;erklärung
        </h1>
        <p className="text-white/60 max-w-2xl leading-relaxed mb-16">
          Wir informieren Sie nachfolgend gemäß Art. 13 DSGVO über die Verarbeitung
          personenbezogener Daten auf dieser Website.
        </p>

        <div className="space-y-10 max-w-3xl">
          <Section n="01" title="Verantwortlicher">
            <p>
              Verantwortlich im Sinne der DSGVO ist:
            </p>
            <p className="text-white" translate="no">
              Martin Fichtelmann<br />
              Berlin-Cube<br />
              Berliner Allee 37–39<br />
              14641 Wustermark
            </p>
            <p>
              Telefon: <a href="tel:+4917613600041" className="text-white hover:text-[#F5D77A]" translate="no">+49 176 13600041</a><br />
              E-Mail: <a href="mailto:info@berlin-cube.de" className="text-white hover:text-[#F5D77A]" translate="no">info@berlin-cube.de</a>
            </p>
          </Section>

          <Section n="02" title="Erhebung und Speicherung personenbezogener Daten">
            <p>
              Beim Aufruf dieser Website werden durch den Browser Ihres Endgeräts automatisch technische Daten an
              den Server der Website gesendet und temporär in einem Logfile gespeichert:
            </p>
            <ul className="list-disc pl-6 space-y-1 text-white/70">
              <li>IP-Adresse des anfragenden Geräts</li>
              <li>Datum und Uhrzeit des Zugriffs</li>
              <li>Name und URL der abgerufenen Datei</li>
              <li>Referrer-URL, verwendeter Browser, Betriebssystem</li>
            </ul>
            <p>
              Rechtsgrundlage: Art. 6 Abs. 1 lit. f DSGVO (berechtigtes Interesse an einer sicheren und stabilen
              Bereitstellung der Website). Speicherdauer: max. 7 Tage.
            </p>
          </Section>

          <Section n="03" title="Kontaktformular">
            <p>
              Wenn Sie uns über das Kontaktformular eine Anfrage senden, werden folgende Daten erhoben:
              Name, Stadt/Gemeinde/Organisation, E-Mail-Adresse, Art des Ortes, ungefähre Länge, Nachricht,
              angekreuzte Unterlagen, Zeitstempel und Sprachversion.
            </p>
            <p>
              Diese Daten verwenden wir ausschließlich zur Beantwortung Ihrer Anfrage und zum Versand der von Ihnen
              gewünschten Unterlagen per E-Mail. Rechtsgrundlage ist Art. 6 Abs. 1 lit. b DSGVO (Durchführung
              vorvertraglicher Maßnahmen auf Ihre Anfrage). Eine Weitergabe an Dritte findet nicht statt.
            </p>
            <p>
              Die Daten werden gelöscht, sobald Ihre Anfrage erledigt ist und keine gesetzliche Aufbewahrungspflicht besteht.
            </p>
            <p>
              Zum Schutz vor automatisierten Eingaben (Spam) nutzt das Formular ein verborgenes Kontrollfeld, das von
              Menschen nicht ausgefüllt wird. Ein externer Captcha-Dienst kommt nicht zum Einsatz; dabei werden keine
              Daten an Dritte übermittelt.
            </p>
          </Section>

          <Section n="04" title="Cookies, Tracking und Analytics">
            <p>
              Diese Website verwendet <span className="text-white">keine</span> Analyse-Cookies, keine Tracking-Pixel und keine
              Web-Analytics-Dienste. Technisch notwendige Session-Informationen (z. B. Sprachauswahl DE/EN)
              werden im Local Storage Ihres Browsers gespeichert und verlassen Ihr Gerät nicht.
            </p>
          </Section>

          <Section n="05" title="Externe Inhalte">
            <p>
              Schriftarten werden von Google Fonts (Google Ireland Ltd., Gordon House, Barrow Street, Dublin 4)
              nachgeladen. Beim Aufruf der Seite überträgt Ihr Browser Ihre IP-Adresse an Google.
              Rechtsgrundlage: Art. 6 Abs. 1 lit. f DSGVO. Details:{" "}
              <a
                href="https://policies.google.com/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#F5D77A] hover:underline"
              >
                policies.google.com/privacy
              </a>.
            </p>
            <p>
              Bilder werden über das Content-Delivery-Netzwerk unseres Hosters ausgeliefert.
              Es werden keine personenbezogenen Daten an weitere Dritte übermittelt.
            </p>
          </Section>

          <Section n="06" title="Ihre Rechte">
            <p>Ihnen stehen als betroffene Person folgende Rechte zu:</p>
            <ul className="list-disc pl-6 space-y-1 text-white/70">
              <li>Auskunft über Ihre bei uns gespeicherten Daten (Art. 15 DSGVO)</li>
              <li>Berichtigung unrichtiger Daten (Art. 16 DSGVO)</li>
              <li>Löschung Ihrer Daten (Art. 17 DSGVO)</li>
              <li>Einschränkung der Verarbeitung (Art. 18 DSGVO)</li>
              <li>Datenübertragbarkeit (Art. 20 DSGVO)</li>
              <li>Widerspruch gegen die Verarbeitung (Art. 21 DSGVO)</li>
              <li>Widerruf erteilter Einwilligungen mit Wirkung für die Zukunft</li>
              <li>Beschwerde bei einer Aufsichtsbehörde (Art. 77 DSGVO) — zuständig: Landesbeauftragte für den Datenschutz und für das Recht auf Akteneinsicht Brandenburg</li>
            </ul>
            <p>
              Zur Ausübung Ihrer Rechte wenden Sie sich formlos an:{" "}
              <a href="mailto:info@berlin-cube.de" className="text-white hover:text-[#F5D77A]">
                info@berlin-cube.de
              </a>.
            </p>
          </Section>

          <Section n="07" title="Datensicherheit">
            <p>
              Diese Website nutzt SSL-/TLS-Verschlüsselung zur Absicherung der Datenübertragung.
              Eine 100 %ige Sicherheit von Daten bei der Übermittlung im Internet kann jedoch nicht gewährleistet werden.
            </p>
          </Section>

          <Section n="08" title="Stand & Aktualisierung">
            <p>
              Stand dieser Datenschutzerklärung: Juli 2026.
              Wir behalten uns vor, diese Datenschutzerklärung anzupassen, um sie an geänderte Rechtslage oder
              Änderungen unserer Angebote anzupassen. Die jeweils aktuelle Version ist unter <span className="text-white">/datenschutz</span> abrufbar.
            </p>
          </Section>
        </div>

        <div className="mt-20 pt-8 border-t border-white/10 flex items-center justify-between text-[11px] tracking-[0.28em] uppercase text-white/40">
          <Link to="/impressum" className="hover:text-[#F5D77A] transition-colors" data-testid="datenschutz-link-impressum">
            ← Impressum
          </Link>
          <Link to="/" className="hover:text-[#F5D77A] transition-colors">
            Startseite →
          </Link>
        </div>
      </main>
      <Footer />
    </div>
  );
}
